import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  Subscriptions as SubscriptionsIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material";
import { blue, green, red, amber, grey, orange } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const PaymentList = ({
  payments = [],
  loading = false,
  pagination = {},
  onViewPayment,
  onViewUser,
  onUpdateStatus,
  onPreviousPage,
  onNextPage,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedPayment, setSelectedPayment] = React.useState(null);

  const handleMenuOpen = (event, payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleViewPayment = () => {
    if (selectedPayment && onViewPayment) {
      onViewPayment(selectedPayment.id);
    }
    handleMenuClose();
  };

  const handleUpdateStatus = () => {
    if (selectedPayment && onUpdateStatus) {
      onUpdateStatus(selectedPayment);
    }
    handleMenuClose();
  };

  const handleViewUser = () => {
    if (selectedPayment && onViewUser && selectedPayment.user_id) {
      onViewUser(selectedPayment.user_id);
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      case "refunded":
        return "info";
      case "canceled":
        return "default";
      default:
        return "default";
    }
  };

  const getPaymentTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "wallet_funding":
        return <WalletIcon fontSize="small" />;
      case "subscription_new":
      case "subscription_renewal":
      case "subscription_change":
        return <SubscriptionsIcon fontSize="small" />;
      default:
        return <CreditCardIcon fontSize="small" />;
    }
  };

  const getPaymentTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "wallet_funding":
        return "primary";
      case "subscription_new":
        return "success";
      case "subscription_renewal":
        return "info";
      case "subscription_change":
        return "warning";
      default:
        return "default";
    }
  };

  const getPaymentTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case "wallet_funding":
        return "Wallet Funding";
      case "subscription_new":
        return "New Subscription";
      case "subscription_renewal":
        return "Renewal";
      case "subscription_change":
        return "Plan Change";
      default:
        return type || "Unknown";
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider?.toLowerCase()) {
      case "stripe":
        return "💳";
      case "binance":
        return "🪙";
      default:
        return "💰";
    }
  };

  const getAccountTypeColor = (accountType) => {
    switch (accountType?.toLowerCase()) {
      case "basic":
        return grey[600];
      case "standard":
        return blue[600];
      case "premium":
        return amber[600];
      case "elite":
        return red[600];
      default:
        return grey[600];
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  if (loading && payments.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && payments.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }} elevation={3}>
        <AttachMoneyIcon sx={{ fontSize: 48, color: grey[400], mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No payments found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your search filters
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: grey[100] }}>
              <TableCell>Payment ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <StyledTableRow key={payment.id}>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      color: "text.primary",
                      wordBreak: "break-all",
                      maxWidth: "200px",
                    }}
                  >
                    {payment.id}
                  </Typography>
                  {payment.provider_payment_id && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        fontSize: "0.7rem",
                        wordBreak: "break-all",
                      }}
                    >
                      {payment.provider_payment_id}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor: getAccountTypeColor(payment.account_type),
                        mr: 2,
                        width: 32,
                        height: 32,
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {payment.username || "Unknown User"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payment.user_email}
                      </Typography>
                      {payment.account_type && (
                        <Chip
                          label={payment.account_type.toUpperCase()}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            ml: 1,
                            bgcolor: getAccountTypeColor(payment.account_type),
                            color: "white",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                      {formatCurrency(payment.amount, payment.currency)}
                    </Typography>
                    {payment.coins_purchased && (
                      <Typography variant="caption" color="text.secondary">
                        {payment.coins_purchased} coins
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getPaymentTypeIcon(payment.payment_type)}
                    label={getPaymentTypeLabel(payment.payment_type)}
                    size="small"
                    color={getPaymentTypeColor(payment.payment_type)}
                    variant="outlined"
                    sx={{ fontSize: "0.7rem" }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>{getProviderIcon(payment.payment_provider)}</span>
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {payment.payment_provider || "Unknown"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={payment.status?.toUpperCase() || "UNKNOWN"}
                    size="small"
                    color={getStatusColor(payment.status)}
                    sx={{
                      fontWeight: "medium",
                      fontSize: "0.7rem",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {formatDate(payment.created_at)}
                    </Typography>
                    {payment.updated_at &&
                      payment.updated_at !== payment.created_at && (
                        <Typography variant="caption" color="text.secondary">
                          Updated: {formatDate(payment.updated_at)}
                        </Typography>
                      )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          onViewPayment && onViewPayment(payment.id)
                        }
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, payment)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            minWidth: 180,
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1.5,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleViewPayment}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Payment Details</ListItemText>
        </MenuItem>

        {selectedPayment?.user_id && (
          <MenuItem onClick={handleViewUser} sx={{ color: green[700] }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: green[700] }} />
            </ListItemIcon>
            <ListItemText>View User Profile</ListItemText>
          </MenuItem>
        )}

        {selectedPayment?.status !== "completed" &&
          selectedPayment?.status !== "refunded" && (
            <MenuItem onClick={handleUpdateStatus} sx={{ color: blue[600] }}>
              <ListItemIcon>
                <EditIcon fontSize="small" sx={{ color: blue[600] }} />
              </ListItemIcon>
              <ListItemText>Update Status</ListItemText>
            </MenuItem>
          )}
      </Menu>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {payments.length} of {pagination.total?.toLocaleString() || 0}{" "}
          payments
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            disabled={!pagination.hasPrev || loading}
            variant="outlined"
            size="small"
            onClick={onPreviousPage}
          >
            Previous
          </Button>
          {pagination.pageNo && pagination.totalPages && (
            <Typography variant="body2" sx={{ px: 2, py: 1 }}>
              Page {pagination.pageNo} of {pagination.totalPages}
            </Typography>
          )}
          <Button
            disabled={!pagination.hasNext || loading}
            variant="outlined"
            size="small"
            onClick={onNextPage}
          >
            Next
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default PaymentList;
